import type { GetDetailsResponse, GetListResponse } from './common';
import type { RoleEntityPermissionMap } from '../../../models/utils/entity-permission.model';
import type { EntityApplications } from '../../../lists/entity-applications.enum';
import type { AssignmentTrigger } from '../../../lists/assignment-trigger.enum';

///////////////////
// Roles
///////////////////
export enum UserRoleResponseKeys {
  id = 'id',
  externalId = 'externalId',
  role = 'role',
  appId = 'appId',
  activity = 'activity',
  isEnable = 'isEnable',
  assignmentTrigger = 'assignmentTrigger',
}

export type UserRoleResponse = {
  /** Row id. Internal to SQL — never stored in another document. */
  id: number;
  /**
   * The id everything else refers to. Seeded from the Firestore document id so the
   * role-id arrays already stored on conference and form-management documents keep
   * resolving after the collection is gone.
   */
  externalId: string;
  role: string;
  appId: EntityApplications;
  /**
   * Typed as the full map for continuity with `UserRole.activity`, but what is
   * actually stored is sparse: the role editor writes only the activity flags it
   * toggles, so most entities are absent and a present entity may carry a subset of
   * the four flags.
   */
  activity: RoleEntityPermissionMap;
  isEnable: boolean;
  assignmentTrigger: AssignmentTrigger;
};

export type UserRoleDetailsResponse = GetDetailsResponse<UserRoleResponse>;

/**
 * Carries the full permission map. The role editor needs it for the entity grid and
 * there are only tens of roles, so shipping it with the list avoids a second fetch
 * per selected role.
 */
export type UserRoleListItemResponse = UserRoleResponse & {
  /** Entities carrying at least one granted activity. */
  grantedEntityCount: number;
  assignedAgentCount: number;
};

export type UserRoleGetListResponse = GetListResponse<UserRoleListItemResponse>;

export type UserRoleUpsertRequest = {
  role: string;
  appId: EntityApplications;
  activity?: RoleEntityPermissionMap;
  isEnable?: boolean;
  assignmentTrigger?: AssignmentTrigger;
  /** Merge into the stored map rather than replacing it, matching the editor's per-toggle saves. */
  mergeActivity?: boolean;
};

///////////////////
// Effective permissions
///////////////////
export type EffectivePermissionsResponse = {
  agentFirebaseDbId: string;
  /**
   * Permissions are per application: a role belongs to exactly one app, and each
   * client only ever merges the roles for its own.
   */
  appId: EntityApplications;
  /** External ids of the enabled, in-app roles that produced the map below. */
  roleIds: string[];
  roleNames: string[];
  /**
   * OR-merged across every contributing role, with no deny-wins rule — the same
   * semantics the four client-side folds implemented, now computed once.
   *
   * For the portal this also carries the `BackgroundEntity` grants derived from the
   * agent's type, which have no role row behind them.
   */
  permissions: RoleEntityPermissionMap;
};

export type EffectivePermissionsDetailsResponse = GetDetailsResponse<EffectivePermissionsResponse>;

///////////////////
// Assignments
///////////////////
/**
 * Deliberately keyed by the `AgentKeys` names rather than by camelCase API names.
 *
 * The row IS an agent as far as the User Roles Manager is concerned, and its grid,
 * its lookups and its role editor were already written against those keys. Emitting
 * them directly means the screen moves from an in-memory Firestore list to
 * server-side paging, filtering and sorting without a template rewrite — and the
 * DevExtreme filters it sends arrive as field names the SQL grid definition can map.
 */
export type UserRoleAssignmentListItemResponse = {
  dbId: string;
  p_agent_id: string | null;
  p_agent_first_name: string | null;
  p_agent_last_name: string | null;
  p_email: string | null;
  agent_status: string | null;
  agent_type: string | null;
  p_agency_id: string | null;
  p_mga_id: string | null;
  is_manager: boolean | null;
  /** Role external ids — the same ids the role editor and the agent documents use. */
  roles: string[];
};

export type UserRoleAssignmentGetListResponse = GetListResponse<UserRoleAssignmentListItemResponse>;

export type UserRoleAssignmentDetailsResponse = GetDetailsResponse<UserRoleAssignmentListItemResponse>;

export enum UserRoleAssignmentMode {
  /** Replace the agent's role set outright — what the single-agent editor does. */
  replace = 'replace',
  /** Union the given roles into whatever each agent already holds — the mass-assign case. */
  add = 'add',
  remove = 'remove',
}

export type UserRoleAssignmentBulkRequest = {
  /** Explicit targets. Mutually exclusive with `filter`. */
  agentFirebaseDbIds?: string[];
  /**
   * A DevExtreme filter expression over the assignment grid, applied server-side.
   *
   * The grid is server-side paged, so "select all matching" can span thousands of
   * agents the client has never loaded — sending the predicate avoids asking it to
   * enumerate ids it does not have. Only columns the grid declares as filterable
   * are honoured; a filter resolving to nothing is REFUSED rather than treated as
   * "match everything".
   */
  filter?: unknown;
  roleIds: string[];
  mode: UserRoleAssignmentMode;
  /**
   * How many agents the caller believed it was affecting. When supplied, a mismatch
   * aborts the write inside its transaction — so a filtered selection that grew
   * between preview and confirm fails loudly instead of quietly hitting more agents
   * than the operator saw.
   */
  expectedAgentCount?: number;
};

export type UserRoleAssignmentBulkResponse = {
  /**
   * Agents the target resolved to that actually exist, whether or not their roles
   * changed. Counted against platform.agents rather than against the ids supplied:
   * it previously counted the caller's input, so an id belonging to no agent was
   * reported as matched and changed.
   */
  matchedAgentCount: number;
  /** Agents whose stored set actually changed. */
  updatedCount: number;
  /**
   * Ids that carried no matching enabled role and were ignored. Returned rather than
   * silently dropped, because the previous implementation swallowed partial failures.
   */
  skippedRoleIds: string[];
  /**
   * Targeted ids that belong to no agent, so nothing was written for them.
   *
   * The assignments table is keyed by the agent id and carries no foreign key, so an
   * unknown id used to insert a row of its own — granting nothing at the time, and
   * silently handing those roles to whoever was later created with that id.
   */
  skippedAgentIds: string[];
};

export type UserRoleAssignmentBulkDetailsResponse = GetDetailsResponse<UserRoleAssignmentBulkResponse>;
